import React, { useState, useEffect, useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
    get_undistributed_products,
    getRawMaterials,
    get_product_details,
    getBankBalance,
    distribute_undistributed_product,
    getUnits,
    getProductMaster,
    list_raw_material_transactions,
    create_product_details,
    get_product_processing
} from "../../../Utility/Api";
import { WarehouseContext } from "../../../Utility/UserContext";
import { NaviGate } from "../../../Utility/function";
import { Pagination } from "../../Common/Functions";

const ProcessStepBar = ({ currentStep, onStepClick }) => {
    const steps = [
        { label: "Create Process", icon: "bi bi-file-earmark-plus" },
        { label: "Manufacturing Details", icon: "bi bi-gear" },
        { label: "Distribution", icon: "bi bi-box-seam" }
    ];

    return (
        <div className="mb-5 mt-3">
            <style>{`
                .stepper-container {
                    position: relative;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    width: 100%;
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 0 0 0.5rem 0;
                }
                .stepper-line {
                    position: absolute;
                    top: 22px;
                    left: 16.67%;
                    right: 16.67%;
                    height: 2px;
                    background-color: #E2E8F0;
                    z-index: 1;
                }
                .stepper-line-progress {
                    position: absolute;
                    top: 0;
                    left: 0;
                    height: 100%;
                    background-color: #3B82F6;
                    transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .stepper-step {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    flex: 1;
                    text-align: center;
                }
                .step-icon-outer {
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: #FFFFFF;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    border: 1.5px solid #E2E8F0;
                }
                .stepper-step.active .step-icon-outer {
                    border: 2px solid #3B82F6;
                    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
                }
                .stepper-step.completed .step-icon-outer {
                    border: 2px solid #10B981;
                }
                .stepper-step.pending .step-icon-outer {
                    border: 1.5px solid #CBD5E1;
                }
                .step-icon-inner {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.95rem;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .stepper-step.active .step-icon-inner {
                    background-color: #3B82F6;
                    color: #FFFFFF;
                }
                .stepper-step.completed .step-icon-inner {
                    background-color: #10B981;
                    color: #FFFFFF;
                }
                .stepper-step.pending .step-icon-inner {
                    background-color: transparent;
                    color: #94A3B8;
                }
                .step-text {
                    margin-top: 12px;
                    font-size: 0.725rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    color: #94A3B8;
                    transition: all 0.3s ease;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    line-height: 1;
                }
                .stepper-step.active .step-text {
                    color: #3B82F6;
                }
                .stepper-step.completed .step-text {
                    color: #10B981;
                }
                .active-dot {
                    width: 5px;
                    height: 5px;
                    border-radius: 50%;
                    background-color: #3B82F6;
                    margin-top: 6px;
                    animation: activePulse 2s infinite ease-in-out;
                }
                @keyframes activePulse {
                    0% { transform: scale(0.8); opacity: 0.5; }
                    50% { transform: scale(1.3); opacity: 1; }
                    100% { transform: scale(0.8); opacity: 0.5; }
                }
            `}</style>

            <div className="stepper-container">
                <div className="stepper-line">
                    <div
                        className="stepper-line-progress"
                        style={{
                            width: `${currentStep === 1 ? 0 : currentStep === 2 ? 50 : 100}%`
                        }}
                    ></div>
                </div>

                {steps.map((step, index) => {
                    const stepNumber = index + 1;
                    const isActive = stepNumber === currentStep;
                    const isCompleted = stepNumber < currentStep;

                    let stepClass = "pending";
                    if (isActive) stepClass = "active";
                    else if (isCompleted) stepClass = "completed";

                    return (
                        <div
                            key={index}
                            className={`stepper-step ${stepClass}`}
                            onClick={() => onStepClick && onStepClick(stepNumber)}
                            style={{ cursor: onStepClick ? 'pointer' : 'default' }}
                        >
                            <div className="step-icon-outer">
                                <div className="step-icon-inner">
                                    {isCompleted ? (
                                        <i className="bi bi-check-lg" style={{ fontSize: "1.1rem" }}></i>
                                    ) : (
                                        <i className={step.icon}></i>
                                    )}
                                </div>
                            </div>
                            <div className="step-text">
                                <span>{step.label}</span>
                                {isActive && <div className="active-dot"></div>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const getMfgDateDisplay = (dates) => {
    const unique = [...new Set(dates.filter(Boolean))].sort();
    if (unique.length === 0) return "-";
    if (unique.length === 1) return unique[0];
    return `${unique[0]} to ${unique[unique.length - 1]}`;
};

const getExpiryDateDisplay = (dates) => {
    const unique = [...new Set(dates.filter(Boolean))].sort();
    if (unique.length === 0) return "-";
    if (unique.length === 1) return unique[0];
    return `${unique[0]} to ${unique[unique.length - 1]}`;
};

const UndistributedProducts = () => {
    const { showLoader, hideLoader, shopItem, UserItem, warehouse } = useContext(WarehouseContext);

    // List State
    const [productsList, setProductsList] = useState([]);

    const groupedProducts = useMemo(() => {
        const groups = {};
        productsList.forEach(batch => {
            const pid = batch.p_id;
            if (!groups[pid]) {
                groups[pid] = {
                    p_id: pid,
                    product_title: batch.product_title,
                    units: batch.units,
                    batches: [],
                    total_qty: 0,
                    total_remaining: 0,
                    mfg_dates: [],
                    expiry_dates: []
                };
            }
            groups[pid].batches.push(batch);
            groups[pid].total_qty += parseFloat(batch.qty || 0);
            groups[pid].total_remaining += parseFloat(batch.remaining || 0);
            if (batch.mfg_date) groups[pid].mfg_dates.push(batch.mfg_date);
            if (batch.expiry_date) groups[pid].expiry_dates.push(batch.expiry_date);
        });
        return Object.values(groups);
    }, [productsList]);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Modal State
    const [showDistributeModal, setShowDistributeModal] = useState(false);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [rawMaterialsList, setRawMaterialsList] = useState([]);
    const [unitsList, setUnitsList] = useState([]);
    const [availableBankBalance, setAvailableBankBalance] = useState(0);
    const [distributionItems, setDistributionItems] = useState([]);

    // Modal Form State (Copied exactly from Product Process Render Step 2)
    const [selectedPackagingId, setSelectedPackagingId] = useState("");
    const [selectedPackagingQty, setSelectedPackagingQty] = useState("");
    const [selectedPackagingPrice, setSelectedPackagingPrice] = useState("");
    const [processDetails, setProcessDetails] = useState(null);

    const [expenseItems, setExpenseItems] = useState([
        { id: 1, description: 'Labour Charges', amount: 0 },
        { id: 2, description: 'Package Charges', amount: 0 },
        { id: 3, description: 'EB/Rent Charges', amount: 0 },
        { id: 4, description: 'Other Charges', amount: 0 }
    ]);
    const [customExpenses, setCustomExpenses] = useState([]);

    const labourCharges = useMemo(() => expenseItems.find(i => i.description === 'Labour Charges')?.amount || 0, [expenseItems]);
    const packageCharges = useMemo(() => expenseItems.find(i => i.description === 'Package Charges')?.amount || 0, [expenseItems]);
    const ebRentCharges = useMemo(() => expenseItems.find(i => i.description === 'EB/Rent Charges')?.amount || 0, [expenseItems]);
    const otherChargesVal = useMemo(() => expenseItems.find(i => i.description === 'Other Charges')?.amount || 0, [expenseItems]);

    const customExpensesTotal = useMemo(() => {
        return customExpenses.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    }, [customExpenses]);

    const handleExpenseChange = (id, value) => {
        const updatedItems = expenseItems.map(item =>
            item.id === id ? { ...item, amount: value } : item
        );
        setExpenseItems(updatedItems);
    };

    // Stepper and ingredients states
    const [productMasterList, setProductMasterList] = useState([]);
    const [modalStep, setModalStep] = useState(3);
    const [ingredientsList, setIngredientsList] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);

    // Raw materials consumed for process in Step 2, and new Package Size modal states
    const [rawMaterialsListForProcess, setRawMaterialsListForProcess] = useState([]);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [formData1, setFormData1] = useState({
        p_id: "",
        p_code: "",
        p_weight: "",
        pbox_unit: "",
        p_price: "",
        pbox_qty: "",
        pbox_code: "",
        pbox_price: ""
    });
    const [errorss, setErrorss] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchUndistributedProducts();
        fetchRawMaterials();
        fetchUnits();
        fetchProductMasterList();
    }, [shopItem, warehouse]);

    useEffect(() => {
        if (selectedBatch && productMasterList.length > 0) {
            fetchIngredientsFromProductMaster(selectedBatch.p_id);
        }
    }, [selectedBatch, productMasterList, rawMaterialsList]);

    const fetchUndistributedProducts = async (page = 1, search = "") => {
        showLoader();
        try {
            const params = {
                shop_id: shopItem?.shop_id || shopItem?.id || "0",
                warehouse_id: warehouse?.id || "1",
                page,
                search
            };
            const res = await get_undistributed_products(params);
            if (res.data && res.data.status === "success") {
                setProductsList(res.data.data);
                setTotalPages(res.data.pagination?.total_pages || 1);
            }
        } catch (error) {
            console.error("Error fetching undistributed products:", error);
            toast.error("Failed to load undistributed products");
        } finally {
            hideLoader();
        }
    };

    const fetchRawMaterials = async () => {
        try {
            const res = await getRawMaterials();
            if (res.data && res.data.data) {
                setRawMaterialsList(res.data.data);
            }
        } catch (error) {
            console.error("Error fetching raw materials:", error);
        }
    };

    const fetchUnits = async () => {
        try {
            const res = await getUnits();
            if (res.data && res.data.data) {
                setUnitsList(res.data.data);
            }
        } catch (error) {
            console.error("Error fetching units:", error);
        }
    };

    const unitMap = useMemo(() => {
        const map = {};
        unitsList.forEach(u => {
            map[String(u.id)] = u.title;
        });
        return map;
    }, [unitsList]);

    const fetchBankBalance = async () => {
        try {
            const shopId = shopItem?.shop_id || shopItem?.id || "";
            const res = await getBankBalance(shopId);
            if (res.data) {
                setAvailableBankBalance(Number(res.data.bank_balance || 0));
            }
        } catch (error) {
            console.error("Error fetching bank balance:", error);
        }
    };

    const fetchProductDetails = async (productId, batchRecord = null) => {
        try {
            const params = {
                p_id: productId,
                shop_id: shopItem?.shop_id || shopItem?.id || ""
            };
            const res = await get_product_details(params);
            if (res.data && res.data.data) {
                const targetBatch = batchRecord || selectedBatch;
                const targetBulkGrams = targetBatch ? Number(targetBatch.remaining) * 1000 : 0;
                const items = res.data.data.map(detail => {
                    const weightInGrams = parseFloat(detail.p_weight || 0);
                    // const singleUnitGrams = convertToGrams(weightInGrams, detail.pbox_unit);
                    // const defaultQty = (res.data.data.length === 1 && singleUnitGrams > 0)
                    //     ? Math.floor(targetBulkGrams / singleUnitGrams)
                    //     : 0;
                    return {
                        id: detail.id,
                        pbox_unit: detail.pbox_unit,
                        product_title: detail.product_title || `${weightInGrams}g Package`,
                        p_weight: weightInGrams,
                        qty: 0,
                        pbox_code: detail.pbox_code,
                        packaging_id: "",
                        packaging_qty: "",
                        packaging_price: "0",
                        packaging_unit: "pcs"
                    };
                });
                const hasFreeSize = items.some(item => item.pbox_code === "FREE SIZE / LOSE QTY");
                if (!hasFreeSize) {
                    const fallbackTitle = items.length > 0 ? items[0].product_title : (targetBatch?.product_title || "Product");
                    items.push({
                        id: "FREE_SIZE",
                        pbox_unit: "kilograms",
                        product_title: fallbackTitle,
                        p_weight: 1,
                        qty: 0,
                        pbox_code: "FREE SIZE / LOSE QTY",
                        packaging_id: "",
                        packaging_qty: "",
                        packaging_price: "0",
                        packaging_unit: "pcs"
                    });
                }

                // Calculate suggested distribution based on weights in grams
                const getItemGrams = (item) => convertToGrams(item.p_weight, item.pbox_unit);
                let remainingWeight = targetBulkGrams;
                const distributedItems = [...items];

                while (remainingWeight > 0 && distributedItems.some(item => getItemGrams(item) <= remainingWeight)) {
                    const itemToUse = distributedItems.find(item => getItemGrams(item) <= remainingWeight);
                    if (!itemToUse) break;

                    const itemGrams = getItemGrams(itemToUse);
                    if (!itemGrams || itemGrams <= 0) break;

                    const itemIndex = distributedItems.findIndex(i => i.id === itemToUse.id);
                    const newQty = (distributedItems[itemIndex].qty || 0) + 1;
                    distributedItems[itemIndex].qty = newQty;
                    if (distributedItems[itemIndex].packaging_id) {
                        distributedItems[itemIndex].packaging_qty = newQty.toString();
                    }
                    remainingWeight -= itemGrams;
                }

                if (remainingWeight > 0) {
                    const losePacksIndex = distributedItems.findIndex(item =>
                        item.pbox_code === "FREE SIZE / LOSE QTY" || item.id === "FREE_SIZE"
                    );
                    if (losePacksIndex !== -1) {
                        const itemGrams = getItemGrams(distributedItems[losePacksIndex]);
                        if (itemGrams > 0) {
                            const extraQty = remainingWeight / itemGrams;
                            const newQty = (distributedItems[losePacksIndex].qty || 0) + extraQty;
                            distributedItems[losePacksIndex].qty = parseFloat(newQty.toFixed(3));
                            if (distributedItems[losePacksIndex].packaging_id) {
                                distributedItems[losePacksIndex].packaging_qty = parseFloat(newQty.toFixed(3)).toString();
                            }
                            remainingWeight = 0;
                        }
                    }
                }

                setDistributionItems(distributedItems);
            }
        } catch (error) {
            console.error("Error fetching product packaging details:", error);
        }
    };

    const fetchProductMasterList = async () => {
        try {
            const res = await getProductMaster();
            if (res.data && res.data.data) {
                setProductMasterList(res.data.data);
            }
        } catch (error) {
            console.error("Error fetching product master list:", error);
        }
    };

    const fetchIngredientsFromProductMaster = (productId) => {
        if (!productId) return;
        try {
            const product = productMasterList.find(p => String(p.product_id) === String(productId));
            if (product && product.ingridents) {
                const ingredientsData = JSON.parse(product.ingridents);
                if (Array.isArray(ingredientsData) && ingredientsData.length > 0) {
                    const enrichedIngredients = ingredientsData.map(ing => {
                        const rawMaterial = rawMaterialsList.find(rm => String(rm.id) === String(ing.id));
                        return {
                            id: ing.id,
                            title: rawMaterial ? rawMaterial.title : `Raw Material #${ing.id}`,
                            remaining_stock: rawMaterial ? rawMaterial.remaining_stock : 0,
                            custom_qty: ing.qty || "",
                            custom_unit: ing.unit || ""
                        };
                    });
                    setIngredientsList(enrichedIngredients);
                    setSelectedProduct(product);
                } else {
                    setIngredientsList([]);
                    setSelectedProduct(product);
                }
            } else {
                setIngredientsList([]);
                setSelectedProduct(null);
            }
        } catch (error) {
            console.error("Error parsing ingredients:", error);
            setIngredientsList([]);
            setSelectedProduct(null);
        }
    };

    const getPackagingCardStyle = () => {
        if (!selectedPackagingId) return {};
        const pMat = rawMaterialsList.find(rm => String(rm.id) === String(selectedPackagingId));
        const unit = unitMap[String(pMat?.unit_id)] || pMat?.unit_id || "pcs";
        if (unit.toLowerCase() === "pcs") {
            return { backgroundColor: "#d1e7dd" }; // beautiful light green
        } else if (unit.toLowerCase() === "kg") {
            return { backgroundColor: "#f8d7da" }; // beautiful light red
        }
        return {};
    };

    const fetchRawMaterialsForProcess = async (processingIds) => {
        if (!processingIds || (Array.isArray(processingIds) && processingIds.length === 0)) {
            setRawMaterialsListForProcess([]);
            return;
        }
        try {
            showLoader();
            const ids = Array.isArray(processingIds) ? processingIds : [processingIds];
            let allTransactions = [];
            const timestamp = new Date().getTime();
            // const response = await list_raw_material_transactions(processingId, timestamp);

            for (const pid of ids) {
                if (!pid) continue;
                const response = await list_raw_material_transactions(pid, timestamp);
                if (response.data && response.data.transactions) {
                    allTransactions = [...allTransactions, ...response.data.transactions];
                }
            }

            // Combine duplicate raw material transactions in the list
            const combinedTx = {};
            allTransactions.forEach(tx => {
                const key = tx.raw_mat_id;
                if (!combinedTx[key]) {
                    combinedTx[key] = { ...tx, qty: 0, price: 0 };
                }
                combinedTx[key].qty += parseFloat(tx.qty || 0);
                combinedTx[key].price += parseFloat(tx.price || 0);
            });

            setRawMaterialsListForProcess(Object.values(combinedTx));
        } catch (error) {
            console.error("Error fetching raw materials for process:", error);
            setRawMaterialsListForProcess([]);
        } finally {
            hideLoader();
        }
    };

    const validateProductDetails = () => {
        const temp = {};
        if (!formData1.p_id) temp.p_id = "Product is required";
        if (!formData1.p_weight) temp.p_weight = "Weight is required";
        if (!formData1.pbox_unit) temp.pbox_unit = "Unit is required";
        if (!formData1.p_price) temp.p_price = "Price is required";

        // Check for duplicate weight and unit in the same product
        const targetPId = formData1.p_id?.toString();
        const targetWeight = parseFloat(formData1.p_weight);
        const targetUnit = formData1.pbox_unit?.trim().toLowerCase();

        if (targetPId && !isNaN(targetWeight) && targetUnit) {
            const isDuplicate = distributionItems.some(detail => {
                // Skip check for "FREE SIZE / LOSE QTY" default details
                if (detail.pbox_code === "FREE SIZE / LOSE QTY" || detail.id === "FREE_SIZE") return false;

                const detWeight = parseFloat(detail.p_weight);
                const detUnit = detail.pbox_unit?.trim().toLowerCase();

                return (
                    !isNaN(detWeight) &&
                    Math.abs(detWeight - targetWeight) < 0.0001 &&
                    detUnit === targetUnit
                );
            });

            if (isDuplicate) {
                temp.p_weight = "This product weight already exists.";
                temp.pbox_unit = "This product weight already exists.";
                toast.error(`Product weight pack '${formData1.p_weight} ${formData1.pbox_unit}' already exists for this product.`);
            }
        }

        setErrorss(temp);
        return Object.keys(temp).length === 0;
    };

    const resetProductDetailsForm = () => {
        setFormData1({
            p_id: "",
            p_code: "",
            p_weight: "",
            pbox_unit: "",
            p_price: "",
            pbox_qty: "",
            pbox_code: "",
            pbox_price: ""
        });
        setErrorss({});
    };

    const handleProductDetailsSubmit = async (e) => {
        e.preventDefault();
        if (!validateProductDetails()) return;

        setLoading(true);
        try {
            const response = await create_product_details(formData1);
            if (response.data && response.data.status === "success") {
                toast.success("Product details added successfully");
                resetProductDetailsForm();
                setShowDetailsModal(false);
                if (selectedBatch) {
                    await fetchProductDetails(selectedBatch.p_id);
                }
            } else {
                toast.error(response.data?.message || "Failed to add product details");
            }
        } catch (error) {
            console.error("Error adding product details:", error);
            toast.error(error?.response?.data?.message || "Error adding product details");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDistributeModal = async (prod) => {
        const combinedBatch = {
            id: prod.batches[0]?.id || "",
            p_id: prod.p_id,
            product_title: prod.product_title,
            qty: prod.total_qty,
            remaining: prod.total_remaining,
            units: prod.units,
            mfg_date: getMfgDateDisplay(prod.mfg_dates),
            expiry_date: getExpiryDateDisplay(prod.expiry_dates),
            batch_no: prod.batches.map(b => b.batch_no).join(", "),
            mfg_cost_overall: prod.batches.reduce((sum, b) => sum + parseFloat(b.mfg_cost_overall || 0), 0),
            mfg_price: prod.batches[0]?.mfg_price || "0",
            sales_price_unit: prod.batches[0]?.sales_price_unit || "0",
            batches: prod.batches
        };

        setSelectedBatch(combinedBatch);
        fetchBankBalance();
        fetchProductDetails(prod.p_id, combinedBatch);

        const processingIds = prod.batches.map(b => b.processing_id).filter(Boolean);
        fetchRawMaterialsForProcess(processingIds);

        // Reset Modal Form
        setSelectedPackagingId("");
        setSelectedPackagingQty("");
        setSelectedPackagingPrice("");
        setExpenseItems([
            { id: 1, description: 'Labour Charges', amount: 0 },
            { id: 2, description: 'Package Charges', amount: 0 },
            { id: 3, description: 'EB/Rent Charges', amount: 0 },
            { id: 4, description: 'Other Charges', amount: 0 }
        ]);
        setCustomExpenses([]);
        setDistributionItems([]);
        setModalStep(3); // default to Step 3
        setShowDistributeModal(true);

        try {
            let combinedProcessDetails = {
                labour_charges: 0,
                package_charges: 0,
                eb_rent_charges: 0,
                other_charges: 0,
                mfg_price_unit: parseFloat(prod.batches[0]?.mfg_price || 0),
                sales_price_unit: parseFloat(prod.batches[0]?.sales_price_unit || 0)
            };

            for (const b of prod.batches) {
                const res = await get_product_processing({ batch_no: b.batch_no });
                if (res.data && res.data.status === "success" && res.data.data && res.data.data.length > 0) {
                    const pd = res.data.data[0];
                    combinedProcessDetails.labour_charges += parseFloat(pd.labour_charges || 0);
                    combinedProcessDetails.package_charges += parseFloat(pd.package_charges || 0);
                    combinedProcessDetails.eb_rent_charges += parseFloat(pd.eb_rent_charges || 0);
                    combinedProcessDetails.other_charges += parseFloat(pd.other_charges || 0);
                }
            }
            setProcessDetails(combinedProcessDetails);
        } catch (error) {
            console.error("Error fetching process details:", error);
            setProcessDetails(null);
        }
    };

    const renderStep1Content = () => {
        if (!selectedBatch) return null;
        return (
            <div className="text-start">
                <div className="row g-3">
                    {/* Product Selection */}
                    <div className="col-md-12">
                        <label className="form-label fw-bold">Selected Product</label>
                        <select className="form-select bg-light" value={selectedBatch.p_id} disabled>
                            <option value={selectedBatch.p_id}>{selectedBatch.product_title}</option>
                        </select>
                    </div>

                    {/* Desired Quantity */}
                    <div className="col-md-4">
                        <label className="form-label fw-bold">Desired Quantity</label>
                        <input
                            type="text"
                            className="form-control bg-light"
                            value={Number(selectedBatch.qty).toFixed(2)}
                            disabled
                        />
                    </div>

                    {/* Unit */}
                    <div className="col-md-4">
                        <label className="form-label fw-bold">Unit</label>
                        <select className="form-select bg-light" value={selectedBatch.units || "kg"} disabled>
                            <option value={selectedBatch.units || "kg"}>{selectedBatch.units === "kg" ? "Kilograms" : selectedBatch.units || "kg"}</option>
                        </select>
                    </div>

                    {/* Manufacturing Date */}
                    <div className="col-md-4">
                        <label className="form-label fw-bold">Manufacturing Date</label>
                        <input
                            type="date"
                            className="form-control bg-light"
                            value={selectedBatch.mfg_date || ""}
                            disabled
                        />
                    </div>
                </div>

                {/* Ingredients Section */}
                {selectedProduct && ingredientsList.length > 0 && (
                    <div className="col-12 mt-4 text-start">
                        <div className="card border">
                            <div className="card-header bg-light">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0 fw-bold">
                                        <i className="bi bi-list-ul me-2"></i>
                                        Required Ingredients for {selectedProduct.title} - Showing data for 1KG
                                    </h6>
                                    <span className="badge bg-primary">
                                        {ingredientsList.length} ingredient(s)
                                    </span>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-bordered table-striped table-sm mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '10%' }}>S.No</th>
                                                <th style={{ width: '50%' }}>Ingredient Name</th>
                                                <th style={{ width: '20%' }}>Quantity</th>
                                                <th style={{ width: '20%' }}>Unit</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ingredientsList.map((ing, index) => (
                                                <tr key={index}>
                                                    <td className="text-center">{index + 1}</td>
                                                    <td>
                                                        <strong>{ing.title}</strong>
                                                        <small className="text-muted ms-1">
                                                            (avbl: {(Number(ing.remaining_stock) || 0).toFixed(2)})
                                                        </small>
                                                    </td>
                                                    <td>{ing.custom_qty}</td>
                                                    <td>{ing.custom_unit}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Navigation Buttons */}
                <div className="col-12 d-flex justify-content-between mt-4">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowDistributeModal(false)}
                    >
                        <i className="bi bi-x-circle me-1"></i>Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setModalStep(2)}
                    >
                        Next: Manufacturing <i className="bi bi-arrow-right ms-1"></i>
                    </button>
                </div>
            </div>
        );
    };

    const renderStep2Content = () => {
        if (!selectedBatch) return null;

        // Find the packaging material transaction inside rawMaterialsListForProcess if any
        const bulkPackagingMat = rawMaterialsListForProcess.find(item => {
            const rm = rawMaterialsList.find(r => String(r.id) === String(item.raw_mat_id));
            return rm && String(rm.type) === "1";
        });

        // Filter out the packaging material transaction from raw materials used table
        const bulkRawMaterialsOnly = rawMaterialsListForProcess.filter(item => {
            const rm = rawMaterialsList.find(r => String(r.id) === String(item.raw_mat_id));
            return !rm || String(rm.type) !== "1";
        });

        const totalBulkRawMaterialCost = bulkRawMaterialsOnly.reduce((sum, item) => sum + parseFloat(item.price || 0), 0);
        const totalBulkPackagingCost = bulkPackagingMat ? parseFloat(bulkPackagingMat.price || 0) : 0;
        const totalBulkExpenses = parseFloat(processDetails?.labour_charges || 0) +
            parseFloat(processDetails?.package_charges || 0) +
            parseFloat(processDetails?.eb_rent_charges || 0) +
            parseFloat(processDetails?.other_charges || 0);
        const totalBulkCost = parseFloat(selectedBatch.mfg_cost_overall || (totalBulkRawMaterialCost + totalBulkPackagingCost + totalBulkExpenses) || 0);

        const mfg_price = parseFloat(processDetails?.mfg_price_unit || selectedBatch?.mfg_price || 0);
        const sales_price = parseFloat(processDetails?.sales_price_unit || selectedBatch?.sales_price_unit || 0);
        const profitPercent = mfg_price > 0 ? Math.round(((sales_price - mfg_price) / mfg_price) * 100) : 0;

        return (
            <div className="text-start">
                {/* Product Info Alert */}
                <div className="alert alert-info">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <strong>Product:</strong> {selectedBatch.product_title}
                        </div>
                        <div>
                            <strong>Desired Qty:</strong> {Number(selectedBatch.qty).toFixed(2)} {selectedBatch.units || 'kg'}
                        </div>
                        {selectedBatch.batch_no && (
                            <div>
                                <strong>Batch #:</strong> {selectedBatch.batch_no}
                            </div>
                        )}
                    </div>
                </div>

                {/* Packaging Material Section */}
                <div className="card mb-4" style={{ backgroundColor: bulkPackagingMat ? "#d1e7dd" : "transparent", transition: "background-color 0.3s ease" }}>
                    <div className="card-header bg-light" style={{ backgroundColor: "transparent" }}>
                        <h6 className="mb-0 text-start">Packaging Material</h6>
                    </div>
                    <div className="card-body">
                        <div className="row g-3 text-start">
                            {/* Material Selection */}
                            <div className="col-md-4">
                                <label className="form-label">Select Packaging Material</label>
                                <select
                                    className="form-select bg-light"
                                    value={bulkPackagingMat ? bulkPackagingMat.raw_mat_id : ""}
                                    disabled
                                    readOnly
                                >
                                    {bulkPackagingMat ? (
                                        <option value={bulkPackagingMat.raw_mat_id}>
                                            {bulkPackagingMat.raw_material_name}
                                        </option>
                                    ) : (
                                        <option value="">No Packaging Material Used</option>
                                    )}
                                </select>
                            </div>

                            {/* Quantity */}
                            <div className="col-md-2">
                                <label className="form-label">Quantity</label>
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control bg-light"
                                        value={bulkPackagingMat ? parseFloat(bulkPackagingMat.qty || 0).toFixed(2) : "0.00"}
                                        disabled
                                        readOnly
                                    />
                                    <span className="input-group-text bg-light text-muted">
                                        {bulkPackagingMat ? (unitMap[String(bulkPackagingMat.unit_id)] || bulkPackagingMat.unit_id || "pcs") : "pcs"}
                                    </span>
                                </div>
                            </div>

                            {/* Unit Price */}
                            <div className="col-md-3">
                                <label className="form-label">Unit Price (₹)</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="text"
                                        className="form-control bg-light"
                                        value={bulkPackagingMat ? parseFloat(bulkPackagingMat.price / bulkPackagingMat.qty || 0).toFixed(2) : "0.00"}
                                        disabled
                                        readOnly
                                    />
                                </div>
                            </div>

                            {/* Total Price */}
                            <div className="col-md-3">
                                <label className="form-label">Total Price (₹)</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="text"
                                        className="form-control bg-light"
                                        value={bulkPackagingMat ? parseFloat(bulkPackagingMat.price || 0).toFixed(2) : "0.00"}
                                        disabled
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Raw Materials Used Section */}
                <div className="card mb-4">
                    <div className="card-header bg-light">
                        <h6 className="mb-0">Raw Materials Used</h6>
                    </div>
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-bordered table-sm">
                                <thead>
                                    <tr>
                                        <th width="50">No.</th>
                                        <th>Material Name</th>
                                        <th width="120">Quantity</th>
                                        <th width="100">Unit</th>
                                        <th width="120">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bulkRawMaterialsOnly && bulkRawMaterialsOnly.length > 0 ? (
                                        bulkRawMaterialsOnly.map((material, index) => (
                                            <tr key={material.id || index}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <div><strong>{material.raw_material_name}</strong></div>
                                                </td>
                                                <td className="text-end">{parseFloat(material.qty || 0).toFixed(2)}</td>
                                                <td>
                                                    {unitMap[material.unit_id] || "-"}
                                                </td>
                                                <td className="text-end">₹{parseFloat(material.price || 0).toFixed(2)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center">
                                                No raw materials data available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr className="table-light">
                                        <td colSpan="4" className="text-end"><strong>Total Raw Material Cost:</strong></td>
                                        <td className="text-end">
                                            <strong>₹{totalBulkRawMaterialCost.toFixed(2)}</strong>
                                        </td>
                                    </tr>
                                    <tr className="table-secondary">
                                        <td colSpan="4" className="text-end"><strong>Overall Cost (Raw + Packaging):</strong></td>
                                        <td className="text-end">
                                            <strong>
                                                ₹{(totalBulkRawMaterialCost + totalBulkPackagingCost).toFixed(2)}
                                            </strong>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Outcome Quantity & Expiry Date (Read-Only) */}
                <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="form-label">Outcome Quantity</label>
                        <div className="input-group">
                            <input
                                type="text"
                                className="form-control bg-light"
                                value={Number(selectedBatch.qty).toFixed(2)}
                                disabled
                                readOnly
                            />
                            <span className="input-group-text">{selectedBatch.units || 'kg'}</span>
                        </div>
                        <small className="text-muted">The actual quantity produced</small>
                    </div>

                    <div className="col-md-6">
                        <label className="form-label">Expiry Date</label>
                        <input
                            type="text"
                            className="form-control bg-light"
                            value={selectedBatch.expiry_date || ""}
                            disabled
                            readOnly
                        />
                        <small className="text-muted">When this product will expire</small>
                    </div>
                </div>

                {/* Manufacturing Expenses Section (Read-Only) */}
                <div className="card mb-4">
                    <div className="card-header bg-light">
                        <h6 className="mb-0">Manufacturing Expenses</h6>
                    </div>
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label">Labour Charges</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="text"
                                        value={parseFloat(processDetails?.labour_charges || 0).toFixed(2)}
                                        className="form-control bg-light"
                                        disabled
                                        readOnly
                                    />
                                </div>
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">EB/Rent Charges</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="text"
                                        value={parseFloat(processDetails?.eb_rent_charges || 0).toFixed(2)}
                                        className="form-control bg-light"
                                        disabled
                                        readOnly
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Other Charges</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="text"
                                        value={parseFloat(processDetails?.other_charges || 0).toFixed(2)}
                                        className="form-control bg-light"
                                        disabled
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Cost Summary Card (Read-Only) */}
                <div className="card mb-4 bg-light border">
                    <div className="card-body">
                        <h6 className="card-title fw-bold mb-3">Manufacturing Cost Summary</h6>
                        <hr className="my-2" />
                        <div className="d-flex justify-content-between mb-2">
                            <h6 className="mb-0 fw-bold">Total Manufacturing Cost:</h6>
                            <h6 className="mb-0 text-primary fw-bold">₹{totalBulkCost.toFixed(2)}</h6>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mt-2">
                            <div className="d-flex align-items-center">
                                <span className="me-2">
                                    Suggested Selling Price (
                                    <input
                                        type="number"
                                        value={profitPercent}
                                        className="form-control d-inline-block bg-light text-center"
                                        style={{ width: "70px", display: "inline-block" }}
                                        disabled
                                        readOnly
                                    />
                                    <span className="ms-1">%</span>
                                    ):
                                </span>
                            </div>

                            <strong className="text-success">
                                {profitPercent
                                    ? `${profitPercent}% → ₹${(totalBulkCost * profitPercent / 100).toFixed(2)}`
                                    : "—"}
                            </strong>
                        </div>
                    </div>
                </div>

                {/* Navigation Buttons */}
                <div className="d-flex justify-content-between mt-4">
                    <div>
                        <button
                            type="button"
                            className="btn btn-outline-secondary me-2"
                            onClick={() => setModalStep(1)}
                        >
                            <i className="bi bi-arrow-left me-1"></i> Back
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => setShowDistributeModal(false)}
                        >
                            <i className="bi bi-x-circle me-1"></i> Cancel Process
                        </button>
                    </div>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setModalStep(3)}
                    >
                        Next: Distribution <i className="bi bi-arrow-right ms-1"></i>
                    </button>
                </div>
            </div>
        );
    };

    const renderStep3Content = () => {
        if (!selectedBatch) return null;

        return (
            <div className="text-start">
                {/* Product Info Alert */}
                <div className="alert alert-info py-2 mb-3 text-start">
                    <div className="d-flex justify-content-between">
                        <span><strong>Product:</strong> {selectedBatch.product_title}</span>
                        <span><strong>Batch:</strong> {selectedBatch.batch_no}</span>
                        <span>
                            <strong>Total Quantity:</strong>{" "}
                            {(() => {
                                const qtyKg = parseFloat(selectedBatch.remaining || 0);
                                const qtyGrams = qtyKg * 1000;
                                return `${qtyGrams.toFixed(0)} grams (${qtyKg.toFixed(2)} kg)`;
                            })()}
                        </span>
                    </div>
                </div>

                {/* Commented out global packaging selection as it is now row-based
                <div className="card mb-4" style={{ ...getPackagingCardStyle(), transition: "background-color 0.3s ease" }}>
                    <div className="card-header bg-light d-flex justify-content-between align-items-center" style={{ backgroundColor: "transparent" }}>
                        <h6 className="mb-0 text-start fw-bold">Distribution Packaging Material</h6>
                    </div>
                    <div className="card-body">
                        <div className="row g-3 text-start">
                            <div className="col-md-4">
                                <label className="form-label">Select Packaging Material</label>
                                <select
                                    className="form-select"
                                    value={selectedPackagingId}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val) {
                                            const rawMat = rawMaterialsList.find(rm => String(rm.id) === String(val));
                                            if (rawMat) {
                                                const match = rawMat.title.match(/^(\d+)\s*(grams?|g|kgs?|kilograms?)?/i);
                                                if (match) {
                                                    const coverSizeGrams = convertToGrams(match[1], match[2]);
                                                    const activeItems = distributionItems.filter(item => parseFloat(item.qty || 0) > 0);
                                                    const allowedWeightsGrams = (activeItems.length > 0 ? activeItems : distributionItems)
                                                        .map(item => convertToGrams(item.p_weight, item.pbox_unit));

                                                    if (!allowedWeightsGrams.includes(coverSizeGrams)) {
                                                        const allowedWeightsStr = (activeItems.length > 0 ? activeItems : distributionItems)
                                                            .map(item => `${item.p_weight} ${item.pbox_unit || 'grams'}`)
                                                            .join(", ");
                                                        toast.error(`Cannot select ${rawMat.title}. The packaging size must match the package weight (${allowedWeightsStr}).`);
                                                        setSelectedPackagingId("");
                                                        setSelectedPackagingPrice("0");
                                                        setSelectedPackagingQty("");
                                                        return;
                                                    }
                                                }
                                            }
                                        }
                                        setSelectedPackagingId(val);
                                        const rawMat = rawMaterialsList.find(rm => String(rm.id) === String(val));
                                        if (rawMat) {
                                            setSelectedPackagingPrice(rawMat.latest_price_unit || "0");
                                            const match = rawMat.title.match(/^(\d+)/);
                                            if (match) {
                                                const coverSizeGrams = parseFloat(match[1]);
                                                const totalQtyKg = parseFloat(selectedBatch.remaining || selectedBatch.qty || 0);
                                                const coverSizeInKg = coverSizeGrams / 1000;
                                                if (coverSizeInKg > 0) {
                                                    const calculatedQty = Math.ceil(totalQtyKg / coverSizeInKg);
                                                    setSelectedPackagingQty(calculatedQty.toString());
                                                }
                                            }
                                        } else {
                                            setSelectedPackagingPrice("0");
                                            setSelectedPackagingQty("");
                                        }
                                    }}
                                >
                                    <option value="">-- Choose Packaging Material --</option>
                                    {rawMaterialsList.filter(rm => String(rm.type) === "1").map(rm => (
                                        <option key={rm.id} value={rm.id} disabled={parseFloat(rm.remaining_stock || 0) <= 0}>
                                            {rm.title} (Stock: {parseFloat(rm.remaining_stock || 0).toFixed(2)} {unitMap[String(rm.unit_id)] || rm.unit_id || 'kg'})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-md-2">
                                <label className="form-label">Quantity</label>
                                <div className="input-group">
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={selectedPackagingQty}
                                        onChange={(e) => setSelectedPackagingQty(e.target.value)}
                                        onWheel={(e) => e.currentTarget.blur()}
                                        placeholder="0.0"
                                        min="0"
                                        step="any"
                                    />
                                    <span className="input-group-text bg-light text-muted">
                                        {packagingUnit}
                                    </span>
                                </div>
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">Unit Price (₹)</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="number"
                                        className="form-control bg-light"
                                        value={selectedPackagingPrice}
                                        placeholder="0.00"
                                        readOnly
                                        disabled
                                    />
                                </div>
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">Total Price (₹)</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="text"
                                        className="form-control bg-light"
                                        value={totalPackagingPrice}
                                        readOnly
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                */}


                {/* Package Distribution Header */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0 fw-bold">Package Distribution</h6>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => {
                            resetProductDetailsForm();
                            setFormData1({
                                p_id: selectedBatch.p_id,
                                p_code: "",
                                p_weight: "",
                                pbox_unit: "",
                                p_price: "",
                                pbox_qty: "",
                                pbox_code: "",
                                pbox_price: ""
                            });
                            setShowDetailsModal(true);
                        }}
                    >
                        <i className="bi bi-plus-circle me-1"></i>Add Package Size
                    </button>
                </div>

                {/* PREVIOUS PACKAGE DISTRIBUTION TABLE STRUCTURE (COMMENTED OUT)
                <div className="card border shadow-none mb-3">
                    <div className="card-body p-0">
                        {distributionItems.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-bordered table-sm mb-0" style={{ width: "100%", fontFamily: "poppins" }}>
                                    <thead className="table-light">
                                        <tr>
                                            <th width="40">No.</th>
                                            <th>Package Type</th>
                                            <th width="120">Weight</th>
                                            <th width="150">Quantity (Packets)</th>
                                            <th width="150">Total Weight</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {distributionItems
                                            .filter(item => {
                                                const isFreeSize = item.pbox_code === "FREE SIZE / LOSE QTY";
                                                const dbHasFreeSize = distributionItems.some(it => it.id !== "FREE_SIZE" && it.pbox_code === "FREE SIZE / LOSE QTY");
                                                if (dbHasFreeSize) {
                                                    if (!selectedPackagingId) {
                                                        return isFreeSize;
                                                    } else {
                                                        return !isFreeSize;
                                                    }
                                                } else {
                                                    return !isFreeSize;
                                                }
                                            })
                                            .map((item, index) => (
                                                <tr key={item.id}>
                                                    <td className="text-center">{index + 1}</td>
                                                    <td className="text-start">{item.product_title}</td>
                                                    <td className="text-center">{item.p_weight + " " + item.pbox_unit}</td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-center"
                                                            value={item.qty || ""}
                                                            onChange={(e) => handleDistributionQtyChange(item.id, e.target.value)}
                                                            onWheel={(e) => e.currentTarget.blur()}
                                                            min="0"
                                                            step="any"
                                                        />
                                                    </td>
                                                    <td className="text-center">
                                                        <strong>
                                                            {(() => {
                                                                const totalGrams = convertToGrams(item.p_weight, item.pbox_unit) * (item.qty || 0);
                                                                if (totalGrams <= 999) {
                                                                    return `${totalGrams} grams`;
                                                                } else {
                                                                    return `${(totalGrams / 1000).toFixed(2)} kg`;
                                                                }
                                                            })()}
                                                        </strong>
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="table-active">
                                            <td colSpan="4" className="text-end fw-bold">Total Distributed:</td>
                                            <td
                                                className={
                                                    totalDistributedGrams > targetBulkGrams
                                                        ? "text-danger fw-bold text-center"
                                                        : Math.abs(totalDistributedGrams - targetBulkGrams) <= 1
                                                            ? "text-success fw-bold text-center"
                                                            : "fw-bold text-center"
                                                }
                                                style={!(totalDistributedGrams > targetBulkGrams) && !(Math.abs(totalDistributedGrams - targetBulkGrams) <= 1) ? { color: "#0d6efd" } : {}}
                                            >
                                                <strong>
                                                    {totalDistributedGrams >= 1000
                                                        ? `${(totalDistributedGrams / 1000).toFixed(3)} kg`
                                                        : `${totalDistributedGrams} grams`
                                                    }
                                                </strong>
                                            </td>
                                        </tr>
                                        <tr className="table-light">
                                            <td colSpan="4" className="text-end fw-bold">Remaining Quantity:</td>
                                            <td
                                                className={
                                                    remainingBulkGrams < 0
                                                        ? "text-danger fw-bold text-center"
                                                        : Math.abs(remainingBulkGrams) <= 1
                                                            ? "text-success text-center fw-bold"
                                                            : "fw-bold text-center"
                                                }
                                                style={!(remainingBulkGrams < 0) && !(Math.abs(remainingBulkGrams) <= 1) ? { color: "#0d6efd" } : {}}
                                            >
                                                <strong>
                                                    {remainingBulkGrams >= 1000
                                                        ? `${(remainingBulkGrams / 1000).toFixed(3)} kg`
                                                        : `${remainingBulkGrams.toFixed(0)} grams`
                                                    }
                                                </strong>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        ) : (
                            <div className="p-3 text-muted text-center small">
                                No package sizes defined for this product master. Please add package details first.
                            </div>
                        )}
                    </div>
                </div>
                */}

                {/* Table-based Packet Sizes */}
                <div className="card border shadow-none mb-3">
                    <div className="card-body p-0">
                        {distributionItems.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-bordered table-sm mb-0" style={{ width: "100%", fontFamily: "poppins" }}>
                                    <thead className="table-light">
                                        <tr>
                                            <th width="40">No.</th>
                                            {/* <th>Package Type</th> */}
                                            <th width="100">Weight</th>
                                            <th width="140">Quantity (Packets)</th>
                                            <th width="140">Total Weight</th>
                                            <th width="240">Packaging Material</th>
                                            <th width="120">Packaging Qty</th>
                                            <th width="110">Unit Price (₹)</th>
                                            <th width="120">Total Price (₹)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {distributionItems
                                            .map((item, index) => (
                                                <tr key={item.id}>
                                                    <td className="text-center">{index + 1}</td>
                                                    {/* <td className="text-start">{item.product_title}</td> */}
                                                    <td className="text-center">
                                                        {item.pbox_code === "FREE SIZE / LOSE QTY" || item.id === "FREE_SIZE" ? "Lose Packs" : item.p_weight + " " + item.pbox_unit}
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-center"
                                                            value={item.qty || ""}
                                                            onChange={(e) => handleDistributionQtyChange(item.id, e.target.value)}
                                                            onWheel={(e) => e.currentTarget.blur()}
                                                            min="0"
                                                            step="any"
                                                        />
                                                    </td>
                                                    <td className="text-center">
                                                        <strong>
                                                            {(() => {
                                                                const totalGrams = convertToGrams(item.p_weight, item.pbox_unit) * (item.qty || 0);
                                                                if (totalGrams <= 999) {
                                                                    return `${totalGrams} grams`;
                                                                } else {
                                                                    return `${(totalGrams / 1000).toFixed(2)} kg`;
                                                                }
                                                            })()}
                                                        </strong>
                                                    </td>
                                                    <td>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={item.packaging_id || ""}
                                                            onChange={(e) => handleRowPackagingIdChange(item.id, e.target.value)}
                                                        >
                                                            <option value="">-- Choose --</option>
                                                            {rawMaterialsList.filter(rm => String(rm.type) === "1").map(rm => (
                                                                <option key={rm.id} value={rm.id} disabled={parseFloat(rm.remaining_stock || 0) <= 0}>
                                                                    {rm.title} (Stock: {parseFloat(rm.remaining_stock || 0).toFixed(2)} {unitMap[String(rm.unit_id)] || rm.unit_id || 'kg'})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <div className="input-group input-group-sm">
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm text-center packaging-field-disabled"
                                                                value={item.packaging_qty !== undefined ? item.packaging_qty : ""}
                                                                onChange={(e) => handleRowPackagingQtyChange(item.id, e.target.value)}
                                                                onWheel={(e) => e.currentTarget.blur()}
                                                                placeholder="0"
                                                                min="0"
                                                                step="any"
                                                                disabled={!item.packaging_id}
                                                            />
                                                            {item.packaging_id && (
                                                                <span className="input-group-text px-1 bg-light text-muted" style={{ fontSize: "0.75rem" }}>
                                                                    {item.packaging_unit || "pcs"}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-center packaging-field-disabled"
                                                            value={item.packaging_price || "0"}
                                                            placeholder="0.00"
                                                            readOnly
                                                            disabled
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm text-center packaging-field-disabled"
                                                            value={(parseFloat(item.packaging_qty || 0) * parseFloat(item.packaging_price || 0)).toFixed(2)}
                                                            readOnly
                                                            disabled
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="table-active">
                                            <td colSpan="3" className="text-end fw-bold">Total Distributed:</td>
                                            <td
                                                className={
                                                    totalDistributedGrams > targetBulkGrams
                                                        ? "text-danger fw-bold text-center"
                                                        : Math.abs(totalDistributedGrams - targetBulkGrams) <= 1
                                                            ? "text-success fw-bold text-center"
                                                            : "fw-bold text-center"
                                                }
                                                style={!(totalDistributedGrams > targetBulkGrams) && !(Math.abs(totalDistributedGrams - targetBulkGrams) <= 1) ? { color: "#0d6efd" } : {}}
                                            >
                                                <strong>
                                                    {totalDistributedGrams >= 1000
                                                        ? `${(totalDistributedGrams / 1000).toFixed(3)} kg`
                                                        : `${totalDistributedGrams} grams`
                                                    }
                                                </strong>
                                            </td>
                                            <td colSpan="3" className="text-end fw-bold">Total Packaging Cost:</td>
                                            <td className="text-center fw-bold text-success">
                                                <strong>₹{parseFloat(totalPackagingPrice).toFixed(2)}</strong>
                                            </td>
                                        </tr>
                                        <tr className="table-light">
                                            <td colSpan="3" className="text-end fw-bold">Remaining Quantity:</td>
                                            <td
                                                className={
                                                    remainingBulkGrams < 0
                                                        ? "text-danger fw-bold text-center"
                                                        : Math.abs(remainingBulkGrams) <= 1
                                                            ? "text-success text-center fw-bold"
                                                            : "fw-bold text-center"
                                                }
                                                style={!(remainingBulkGrams < 0) && !(Math.abs(remainingBulkGrams) <= 1) ? { color: "#0d6efd" } : {}}
                                            >
                                                <strong>
                                                    {remainingBulkGrams >= 1000
                                                        ? `${(remainingBulkGrams / 1000).toFixed(3)} kg`
                                                        : `${remainingBulkGrams.toFixed(0)} grams`
                                                    }
                                                </strong>
                                            </td>
                                            <td colSpan="4"></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        ) : (
                            <div className="p-3 text-muted text-center small">
                                No package sizes defined for this product master. Please add package details first.
                            </div>
                        )}
                    </div>
                </div>

                {/* Distribution Expenses Section */}
                <div className="card mb-4">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 fw-bold">Distribution Expenses / Charges</h6>
                        <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-secondary text-white small">Bank Balance: ₹{availableBankBalance.toFixed(2)}</span>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                    const newId = customExpenses.length > 0
                                        ? Math.max(...customExpenses.map(item => item.id)) + 1
                                        : 1;

                                    setCustomExpenses([
                                        ...customExpenses,
                                        { id: newId, description: '', amount: 0 }
                                    ]);
                                }}
                            >
                                <i className="bi bi-plus-circle me-1"></i> Add Custom Charge
                            </button>
                        </div>
                    </div>

                    <div className="card-body">
                        <div className="row g-3">
                            {/* Standard expense items */}
                            {expenseItems.map((item) => {
                                if (item.description !== 'Other Charges') {
                                    return null;
                                }
                                return (
                                    <div className="col-md-12" key={item.id}>
                                        <label className="form-label">{item.description}</label>
                                        <div className="input-group">
                                            <span className="input-group-text">₹</span>
                                            <input
                                                type="number"
                                                value={item.amount}
                                                onChange={(e) => handleExpenseChange(item.id, e.target.value)}
                                                onWheel={(e) => e.currentTarget.blur()}
                                                className="form-control"
                                                placeholder="0.00"
                                                min="0"
                                                step="1"
                                            />
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Custom expense items with delete option */}
                            {customExpenses.map((item) => (
                                <div className="col-md-6" key={`custom-${item.id}`}>
                                    <div className="mb-2">
                                        <input
                                            type="text"
                                            value={item.description}
                                            onChange={(e) => {
                                                const updatedItems = customExpenses.map(i =>
                                                    i.id === item.id ? { ...i, description: e.target.value } : i
                                                );
                                                setCustomExpenses(updatedItems);
                                            }}
                                            className="form-control mb-2"
                                            placeholder="Enter expense title"
                                        />
                                    </div>
                                    <div className="input-group">
                                        <span className="input-group-text">₹</span>
                                        <input
                                            type="number"
                                            value={item.amount}
                                            onChange={(e) => {
                                                const updatedItems = customExpenses.map(i =>
                                                    i.id === item.id ? { ...i, amount: e.target.value } : i
                                                );
                                                setCustomExpenses(updatedItems);
                                            }}
                                            onWheel={(e) => e.currentTarget.blur()}
                                            className="form-control"
                                            placeholder="0.00"
                                            min="0"
                                            step="1"
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger"
                                            onClick={() => {
                                                const updatedItems = customExpenses.filter(i => i.id !== item.id);
                                                setCustomExpenses(updatedItems);
                                            }}
                                        >
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Distribution Cost Summary */}
                <div className="card mb-4 bg-light border">
                    <div className="card-body">
                        <h6 className="card-title fw-bold mb-3">Distribution Cost Summary</h6>
                        <div className="d-flex justify-content-between mb-2">
                            <span>Original Bulk Stock Cost:</span>
                            <strong>₹{Number(selectedBatch.mfg_cost_overall || 0).toFixed(2)}</strong>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                            <span>Extra Cost (Distribution Packaging & Expenses):</span>
                            <strong>₹{totalDistributionCost.toFixed(2)}</strong>
                        </div>
                        <hr className="my-2" />
                        <div className="d-flex justify-content-between mb-0">
                            <h6 className="mb-0 fw-bold">Final Distributed Cost:</h6>
                            <h6 className="mb-0 text-primary fw-bold">
                                ₹{(Number(selectedBatch.mfg_cost_overall || 0) + totalDistributionCost).toFixed(2)}
                            </h6>
                        </div>
                    </div>
                </div>

                {/* Navigation Buttons */}
                <div className="d-flex justify-content-between mt-4">
                    <div>
                        <button
                            type="button"
                            className="btn btn-outline-secondary me-2"
                            onClick={() => setModalStep(2)}
                        >
                            <i className="bi bi-arrow-left me-1"></i> Back
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => setShowDistributeModal(false)}
                        >
                            <i className="bi bi-x-circle me-1"></i> Cancel Process
                        </button>
                    </div>
                    <button
                        type="submit"
                        className="btn btn-warning text-dark px-4 fw-bold shadow-sm"
                        disabled={totalDistributedGrams <= 0 || totalDistributedGrams > targetBulkGrams + (targetBulkGrams * 0.001)}
                    >
                        Finalize Distribution
                    </button>
                </div>
            </div>
        );
    };


    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        fetchUndistributedProducts(pageNumber);
    };

    const handleDistributionQtyChange = (id, val) => {
        if (val === "") {
            setDistributionItems(prev => prev.map(item =>
                item.id === id ? { ...item, qty: "", packaging_qty: "" } : item
            ));
            return;
        }
        const newQty = parseFloat(val) || 0;
        if (newQty < 0) return;
        setDistributionItems(prev => prev.map(item =>
            item.id === id ? { ...item, qty: val, packaging_qty: item.packaging_id ? val : item.packaging_qty } : item
        ));
    };

    const handleRowPackagingIdChange = (id, val) => {
        setDistributionItems(prev => prev.map(item => {
            if (item.id === id) {
                if (!val) {
                    return {
                        ...item,
                        packaging_id: "",
                        packaging_qty: "",
                        packaging_price: "0",
                        packaging_unit: "pcs"
                    };
                }
                const rawMat = rawMaterialsList.find(rm => String(rm.id) === String(val));
                if (rawMat) {
                    const price = rawMat.latest_price_unit || "0";
                    const unit = unitMap[String(rawMat.unit_id)] || rawMat.unit_id || "pcs";

                    const match = rawMat.title.match(/^(\d+)\s*(grams?|g|kgs?|kilograms?)?/i);
                    if (match && item.id !== "FREE_SIZE" && item.pbox_code !== "FREE SIZE / LOSE QTY") {
                        const coverSizeGrams = convertToGrams(match[1], match[2]);
                        const packageWeightGrams = convertToGrams(item.p_weight, item.pbox_unit);
                        if (packageWeightGrams !== coverSizeGrams) {
                            toast.error(`Cannot select ${rawMat.title}. The packaging size must match the package weight (${item.p_weight} ${item.pbox_unit || 'grams'}).`);
                            return item;
                        }
                    }

                    return {
                        ...item,
                        packaging_id: val,
                        packaging_price: price,
                        packaging_qty: item.qty ? item.qty.toString() : "",
                        packaging_unit: unit
                    };
                }
            }
            return item;
        }));
    };

    const handleRowPackagingQtyChange = (id, val) => {
        if (val === "") {
            setDistributionItems(prev => prev.map(item =>
                item.id === id ? { ...item, packaging_qty: "" } : item
            ));
            return;
        }
        const newQty = parseFloat(val) || 0;
        if (newQty < 0) return;
        setDistributionItems(prev => prev.map(item =>
            item.id === id ? { ...item, packaging_qty: val } : item
        ));
    };

    const convertToGrams = (value, unit) => {
        const val = parseFloat(value || 0);
        if (!unit) return val;
        const normalized = unit.toLowerCase();

        switch (normalized) {
            case "kg":
            case "kilogram":
            case "kilograms":
                return val * 1000;
            case "mg":
            case "milligram":
            case "milligrams":
                return val / 1000;
            case "g":
            case "gram":
            case "grams":
            default:
                return val;
        }
    };

    // Calculate total weight of distributed packets in grams
    const totalDistributedGrams = useMemo(() => {
        let total = 0;
        distributionItems.forEach(item => {
            const weightInGrams = convertToGrams(item.p_weight, item.pbox_unit);
            // const qty = parseInt(item.qty || 0);
            const qty = parseFloat(item.qty || 0);
            total += weightInGrams * qty;
        });
        return total;
    }, [distributionItems]);

    const targetBulkGrams = selectedBatch ? Number(selectedBatch.remaining) * 1000 : 0;
    const remainingBulkGrams = targetBulkGrams - totalDistributedGrams;

    // Derived states for packaging material selection (Copied exactly from Product Process Render Step 2)
    const selectedPackagingMat = useMemo(() => {
        return rawMaterialsList.find(rm => String(rm.id) === String(selectedPackagingId));
    }, [selectedPackagingId, rawMaterialsList]);

    const packagingUnit = useMemo(() => {
        if (!selectedPackagingMat) return "kg";
        return unitMap[String(selectedPackagingMat.unit_id)] || selectedPackagingMat.unit_id || "kg";
    }, [selectedPackagingMat, unitMap]);

    const totalPackagingPrice = useMemo(() => {
        let total = 0;
        distributionItems.forEach(item => {
            if (item.packaging_id) {
                total += parseFloat(item.packaging_qty || 0) * parseFloat(item.packaging_price || 0);
            }
        });
        return total.toFixed(2);
    }, [distributionItems]);

    // Calculate total packaging and other charges to check bank balance
    const totalDistributionCost = useMemo(() => {
        const lc = parseFloat(labourCharges || 0);
        const er = parseFloat(ebRentCharges || 0);
        const oc = parseFloat(otherChargesVal || 0);
        const pkg = parseFloat(totalPackagingPrice || 0);
        return lc + er + oc + pkg;
    }, [labourCharges, ebRentCharges, otherChargesVal, totalPackagingPrice]);

    const handleSubmitDistribution = async (e) => {
        e.preventDefault();

        if (totalDistributedGrams <= 0) {
            toast.warn("Please distribute at least one packet size");
            return;
        }

        const tolerance = Math.max(targetBulkGrams * 0.001, 1); // 0.1% tolerance or 1g
        if (totalDistributedGrams > targetBulkGrams + tolerance) {
            toast.error("Distributed weight exceeds the allowed quantity");
            return;
        }

        const bankChargesOnly = parseFloat(labourCharges || 0) +
            parseFloat(ebRentCharges || 0) +
            parseFloat(otherChargesVal || 0) +
            parseFloat(packageCharges || 0) +
            customExpensesTotal;

        if (bankChargesOnly > 0 && bankChargesOnly > availableBankBalance) {
            toast.error(`Insufficient bank balance. Required: ₹${bankChargesOnly.toFixed(2)}, Available: ₹${availableBankBalance.toFixed(2)}`);
            return;
        }

        // Validate packaging material stock per row
        const packagingUsage = {};
        for (const item of distributionItems) {
            if (item.packaging_id) {
                const qtyVal = parseFloat(item.packaging_qty || 0);
                if (qtyVal <= 0) {
                    const label = (item.pbox_code === "FREE SIZE / LOSE QTY" || item.id === "FREE_SIZE") ? "Lose Packs" : `${item.p_weight} ${item.pbox_unit}`;
                    toast.error(`Please enter a valid packaging quantity for ${label}.`);
                    return;
                }
                const matId = String(item.packaging_id);
                packagingUsage[matId] = (packagingUsage[matId] || 0) + qtyVal;

                // Weight match verification
                if (item.id !== "FREE_SIZE" && item.pbox_code !== "FREE SIZE / LOSE QTY") {
                    const rawMat = rawMaterialsList.find(rm => String(rm.id) === matId);
                    if (rawMat) {
                        const match = rawMat.title.match(/^(\d+)\s*(grams?|g|kgs?|kilograms?)?/i);
                        if (match) {
                            const coverSizeGrams = convertToGrams(match[1], match[2]);
                            const packageWeightGrams = convertToGrams(item.p_weight, item.pbox_unit);
                            if (packageWeightGrams !== coverSizeGrams) {
                                toast.error(`Selected packaging material (${rawMat.title}) does not match the package weight (${item.p_weight} ${item.pbox_unit || 'grams'}).`);
                                return;
                            }
                        }
                    }
                }
            }
        }

        for (const [matId, qtyUsed] of Object.entries(packagingUsage)) {
            const rawMat = rawMaterialsList.find(rm => String(rm.id) === matId);
            if (rawMat) {
                const availableStock = parseFloat(rawMat.remaining_stock || 0);
                if (qtyUsed > availableStock) {
                    const unit = unitMap[String(rawMat.unit_id)] || rawMat.unit_id || "pcs";
                    toast.error(`Insufficient stock for ${rawMat.title}! Total needed: ${qtyUsed.toFixed(2)}, Available: ${availableStock.toFixed(2)} ${unit}`);
                    return;
                }
            }
        }

        showLoader();
        try {
            // const batch_distribution = distributionItems
            //     .filter(item => item.qty > 0)
            //     .map(item => ({
            // Sort batches oldest first (FIFO based on batch_no)
            const sortedBatches = [...selectedBatch.batches].sort((a, b) => {
                return String(a.batch_no).localeCompare(String(b.batch_no));
            });

            // Initialize capacity for each batch in kg
            sortedBatches.forEach(b => {
                b.capacity = parseFloat(b.remaining || 0);
                b.allocatedItems = [];
            });

            // Loop through each distribution item and allocate to batches in FIFO order
            distributionItems.forEach(item => {
                let qtyToAllocate = parseFloat(item.qty || 0);
                if (qtyToAllocate <= 0) return;

                const packetWeightKg = convertToGrams(item.p_weight, item.pbox_unit) / 1000;

                for (let i = 0; i < sortedBatches.length; i++) {
                    if (qtyToAllocate <= 0) break;
                    const batch = sortedBatches[i];
                    if (batch.capacity <= 0) continue;

                    // Max packets of this size that can fit in this batch's remaining capacity
                    let fitPackets = Math.floor(batch.capacity / packetWeightKg);

                    let allocated = Math.min(qtyToAllocate, fitPackets);
                    if (allocated > 0) {
                        batch.allocatedItems.push({ id: item.id, qty: allocated });
                        batch.capacity = parseFloat((batch.capacity - allocated * packetWeightKg).toFixed(4));
                        qtyToAllocate -= allocated;
                    }
                }

                // If there's still a tiny remainder (due to flooring or floating-point rounding),
                // add it to the first batch that was allocated this item, or the first batch that has any capacity.
                if (qtyToAllocate > 0) {
                    const targetBatch = sortedBatches.find(b => b.capacity > 0) || sortedBatches[0];
                    const existing = targetBatch.allocatedItems.find(x => x.id === item.id);
                    if (existing) {
                        existing.qty += qtyToAllocate;
                    } else {
                        targetBatch.allocatedItems.push({ id: item.id, qty: qtyToAllocate });
                    }
                    targetBatch.capacity = parseFloat((targetBatch.capacity - qtyToAllocate * packetWeightKg).toFixed(4));
                    qtyToAllocate = 0;
                }
            });

            const batchesToCall = sortedBatches.filter(b => b.allocatedItems && b.allocatedItems.length > 0);

            for (let idx = 0; idx < batchesToCall.length; idx++) {
                const batch = batchesToCall[idx];
                const isFirst = idx === 0;

                const batch_distribution = batch.allocatedItems.map(item => ({
                    id: item.id,
                    qty: Number(item.qty)
                }));

                const payload = {
                    // product_transaction_id: selectedBatch.id,
                    product_transaction_id: batch.id,
                    batch_distribution,
                    packaging_materials: isFirst ? distributionItems
                        .filter(item => item.packaging_id && parseFloat(item.packaging_qty || 0) > 0)
                        .map(item => ({
                            raw_mat_id: item.packaging_id,
                            qty: parseFloat(item.packaging_qty || 0),
                            unit: item.packaging_unit || "pcs"
                        })) : [],
                    other_charges: isFirst ? parseFloat(otherChargesVal || 0) : 0,
                    package_charges: isFirst ? parseFloat(packageCharges || 0) : 0,
                    eb_rent_charges: isFirst ? parseFloat(ebRentCharges || 0) : 0,
                    labour_charges: isFirst ? parseFloat(labourCharges || 0) : 0,
                    custom_expenses: isFirst ? customExpenses.map(item => ({
                        title: item.description,
                        amount: parseFloat(item.amount || 0)
                    })) : [],
                    created_by: UserItem?.user_id || "1",
                    shop_id: shopItem?.shop_id || shopItem?.id || "0",
                    branch_id: UserItem?.user_id || "1",
                    warehouse_id: warehouse?.id || "1"
                };

                const res = await distribute_undistributed_product(payload);
                if (!res.data || res.data.status !== "success") {
                    throw new Error(res.data?.message || `Failed to distribute batch ${batch.batch_no}`);
                }
            }

            toast.success("Product distributed successfully!");
            setShowDistributeModal(false);
            fetchUndistributedProducts(currentPage);
        } catch (error) {
            console.error("Error distributing product:", error);
            const errorMsg = error?.response?.data?.message || error.message || "An error occurred during distribution";
            toast.error(errorMsg);
        } finally {
            hideLoader();
        }
    };

    return (
        <div className="page-wrappers">
            <style>{`
              .modal-body .form-control.packaging-field-disabled:disabled,
              .modal-body .form-control.packaging-field-disabled[readonly] {
                background-color: rgba(233, 236, 239, 1) !important;
                color: #4a4e52 !important;
              }
            `}</style>
            <div className="page-content">
                <NaviGate />
                <div className="mb-0 d-flex justify-content-between align-items-center">
                    <h6 className="mb-0 text-uppercase" style={{ fontFamily: "poppins" }}>
                        Undistributed Product Management
                    </h6>
                </div>
                <hr className="mt-1" />

                <div className="card">
                    <div className="card-body">


                        {/* List Table */}
                        <div className="table-responsive">
                            <table className="table table-striped table-sm table-bordered" style={{ width: "100%", fontFamily: "poppins" }}>
                                <thead>
                                    <tr>
                                        <th>S.No</th>
                                        <th>Product Name</th>
                                        {/* <th>Batch No</th> */}
                                        <th>Mfg Qty</th>
                                        <th>Remaining Bulk Qty</th>
                                        <th>Mfg Date</th>
                                        <th>Expiry Date</th>
                                        <th className="text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupedProducts.length > 0 ? (
                                        groupedProducts.map((prod, index) => (
                                            <tr key={prod.p_id}>
                                                <td>{(currentPage - 1) * 10 + index + 1}</td>
                                                <td className="fw-semibold">
                                                    <div>{prod.product_title || "Unknown Product"}</div>
                                                    <div className="d-flex flex-column align-items-center gap-1 mt-2">
                                                        {prod.batches.map(b => (
                                                            <span key={b.id} className="badge bg-light text-dark border fw-normal" style={{ fontSize: "0.75rem" }}>
                                                                {b.batch_no}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                {/* <td>
                                                    {prod.batches.map(b => (
                                                        <span key={b.id} className="badge bg-light text-dark border me-1 mb-1">
                                                            {b.batch_no}
                                                        </span>
                                                    ))}
                                                </td> */}
                                                <td>{Number(prod.total_qty).toFixed(2)} {prod.units || "kg"}</td>
                                                <td>
                                                    <span className="badge bg-light text-success border fw-bold">
                                                        {Number(prod.total_remaining).toFixed(3)} {prod.units || "kg"}
                                                    </span>
                                                </td>
                                                <td>{getMfgDateDisplay(prod.mfg_dates)}</td>
                                                <td>{getExpiryDateDisplay(prod.expiry_dates)}</td>
                                                <td className="text-center">
                                                    <button
                                                        className="btn btn-sm btn-warning text-dark px-3 py-1 rounded-pill shadow-sm border-0 d-inline-flex align-items-center gap-1"
                                                        onClick={() => handleOpenDistributeModal(prod)}
                                                    >
                                                        <i className="bi bi-box-arrow-up-right"></i>
                                                        <span className="fw-bold small">Distribute</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="text-center py-3">No undistributed bulk products found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    </div>
                </div>
                {/* Distribute Modal */}
                {showDistributeModal && selectedBatch && (
                    <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
                        <div className="modal-dialog modal-xl modal-dialog-scrollable">
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "8px" }}>
                                <div className="modal-header bg-light d-flex justify-content-between align-items-center border-bottom">
                                    <div className="d-flex align-items-center gap-2">
                                        <h5 className="modal-title mb-0 fw-bold text-dark" style={{ fontFamily: "poppins" }}>
                                            {modalStep === 1 ? "Create Process Details" : modalStep === 2 ? "Manufacturing Details" : "Product Distribution"}
                                        </h5>
                                        <span className="badge bg-primary py-2 px-3">
                                            Batch #{selectedBatch.batch_no} - {modalStep === 1 ? 'Creation' : modalStep === 2 ? 'Manufacturing' : 'Distribution'} Step
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowDistributeModal(false)}
                                    ></button>
                                </div>

                                <div className="modal-body p-4 text-start">
                                    {/* Copying Stepper Design from Product_process.js highlighting Step 3 */}
                                    <ProcessStepBar currentStep={modalStep} onStepClick={setModalStep} />

                                    <form onSubmit={handleSubmitDistribution}>
                                        {modalStep === 1 && renderStep1Content()}
                                        {modalStep === 2 && renderStep2Content()}
                                        {modalStep === 3 && renderStep3Content()}
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Product Details Modal */}
                {showDetailsModal && (
                    <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }} tabIndex="-2">
                        <div className="modal-dialog modal-lg">
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "16px" }}>
                                <div className="modal-header bg-dark text-white border-0" style={{ borderTopLeftRadius: "16px", borderTopRightRadius: "16px" }}>
                                    <h5 className="modal-title d-flex align-items-center gap-2">
                                        <i className="bi bi-plus-circle-fill"></i>
                                        <span>Add Product Package Details</span>
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => {
                                            setShowDetailsModal(false);
                                            resetProductDetailsForm();
                                        }}
                                    ></button>
                                </div>

                                <div className="modal-body p-4 text-start">
                                    <form onSubmit={handleProductDetailsSubmit}>
                                        <div className="row g-3">
                                            {/* Product Selection */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">Select Product <span className="text-danger">*</span></label>
                                                <select
                                                    name="p_id"
                                                    value={formData1.p_id}
                                                    className={`form-control ${errorss.p_id ? "is-invalid" : ""}`}
                                                    disabled
                                                >
                                                    <option value={selectedBatch?.p_id}>{selectedBatch?.product_title}</option>
                                                </select>
                                                {errorss.p_id && <div className="invalid-feedback">{errorss.p_id}</div>}
                                            </div>

                                            {/* Product Code */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">Product Code</label>
                                                <input
                                                    type="text"
                                                    name="p_code"
                                                    value={formData1.p_code}
                                                    onChange={(e) => setFormData1(prev => ({ ...prev, p_code: e.target.value }))}
                                                    className="form-control"
                                                />
                                            </div>

                                            {/* Product Weight */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">Weight <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    name="p_weight"
                                                    value={formData1.p_weight}
                                                    onChange={(e) => setFormData1(prev => ({ ...prev, p_weight: e.target.value }))}
                                                    className={`form-control ${errorss.p_weight ? "is-invalid" : ""}`}
                                                    placeholder="0.00"
                                                />
                                                {errorss.p_weight && <div className="invalid-feedback">{errorss.p_weight}</div>}
                                            </div>

                                            {/* Unit */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">Unit <span className="text-danger">*</span></label>
                                                <select
                                                    name="pbox_unit"
                                                    value={formData1.pbox_unit}
                                                    onChange={(e) => setFormData1(prev => ({ ...prev, pbox_unit: e.target.value }))}
                                                    className={`form-control ${errorss.pbox_unit ? "is-invalid" : ""}`}
                                                >
                                                    <option value="">Select Unit</option>
                                                    <option value="grams">Grams</option>
                                                    <option value="kilograms">Kilograms</option>
                                                </select>
                                                {errorss.pbox_unit && <div className="invalid-feedback">{errorss.pbox_unit}</div>}
                                            </div>

                                            {/* Price */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">Price <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    name="p_price"
                                                    value={formData1.p_price}
                                                    onChange={(e) => setFormData1(prev => ({ ...prev, p_price: e.target.value }))}
                                                    className={`form-control ${errorss.p_price ? "is-invalid" : ""}`}
                                                    placeholder="0.00"
                                                />
                                                {errorss.p_price && <div className="invalid-feedback">{errorss.p_price}</div>}
                                            </div>

                                            {/* Box Quantity */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">Box Quantity</label>
                                                <input
                                                    type="number"
                                                    name="pbox_qty"
                                                    value={formData1.pbox_qty}
                                                    onChange={(e) => setFormData1(prev => ({ ...prev, pbox_qty: e.target.value }))}
                                                    onWheel={(e) => e.currentTarget.blur()}
                                                    className={`form-control ${errorss.pbox_qty ? "is-invalid" : ""}`}
                                                />
                                                {errorss.pbox_qty && <div className="invalid-feedback">{errorss.pbox_qty}</div>}
                                            </div>

                                            {/* Box Code */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">Box Code</label>
                                                <input
                                                    type="text"
                                                    name="pbox_code"
                                                    value={formData1.pbox_code}
                                                    onChange={(e) => setFormData1(prev => ({ ...prev, pbox_code: e.target.value }))}
                                                    className="form-control"
                                                />
                                            </div>

                                            {/* Box Price */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">Box Price</label>
                                                <input
                                                    type="number"
                                                    name="pbox_price"
                                                    value={formData1.pbox_price}
                                                    onChange={(e) => setFormData1(prev => ({ ...prev, pbox_price: e.target.value }))}
                                                    onWheel={(e) => e.currentTarget.blur()}
                                                    className="form-control"
                                                    step="1"
                                                />
                                            </div>
                                        </div>

                                        <div className="modal-footer border-0 p-0 mt-4 d-flex gap-2 justify-content-end">
                                            <button
                                                type="button"
                                                className="btn btn-secondary px-4"
                                                onClick={() => {
                                                    setShowDetailsModal(false);
                                                    resetProductDetailsForm();
                                                }}
                                            >
                                                Cancel
                                            </button>
                                            <button type="submit" className="btn btn-primary px-4" disabled={loading}>
                                                {loading ? "Saving..." : "Save Details"}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UndistributedProducts;
